// Revolt Segmented Control Component
// Professional segmented control with sliding background animation

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import './RevoltSegmentedControl.css';

export function RevoltSegmentedControl({
  options = [],
  value,
  onChange,
  size = 'default', // 'sm' | 'default'
  disabled = false,
  disableAnimation = false,
  equalWidthPadding = 8, // Extra padding around text (pixels)
  fitContent = false, // If true, each option is sized to fit its content instead of equal width
  className = '',
  ...props
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [sliderStyle, setSliderStyle] = useState({});
  const [equalWidth, setEqualWidth] = useState(85); // Minimum width
  const [isInitialized, setIsInitialized] = useState(false);
  const containerRef = useRef(null);
  const optionRefs = useRef([]);

  // Initialize refs array
  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, options.length);
  }, [options.length]);

  // Update active index when value changes
  useEffect(() => {
    const index = options.findIndex(option => option.value === value);
    if (index !== -1) {
      setActiveIndex(index);
    }
  }, [value, options]);

  // Calculate equal width based on largest option (only when fitContent is false)
  const calculateEqualWidth = useCallback(() => {
    if (optionRefs.current.length === 0) return;

    if (fitContent) {
      // When fitContent is true, just mark as initialized without calculating widths
      setIsInitialized(true);
      return;
    }

    let maxWidth = 85; // Minimum width
    optionRefs.current.forEach(ref => {
      if (ref) {
        // Temporarily remove inline width to get natural width
        const currentWidth = ref.style.width;
        ref.style.width = 'auto';
        const naturalWidth = ref.scrollWidth;
        ref.style.width = currentWidth; // Restore width

        // scrollWidth includes CSS padding, so we just add extra configurable padding
        maxWidth = Math.max(maxWidth, naturalWidth + (equalWidthPadding * 2));
      }
    });

    setEqualWidth(maxWidth);
    setIsInitialized(true);
  }, [equalWidthPadding, fitContent]);

  // Update slider position when active index changes
  const updateSliderPosition = useCallback(() => {
    if (!containerRef.current) return;

    const activeOption = optionRefs.current[activeIndex];
    if (activeOption && activeOption.offsetWidth > 0) {
      // Precise calculation when DOM is ready and option has dimensions
      const containerRect = containerRef.current.getBoundingClientRect();
      const optionRect = activeOption.getBoundingClientRect();

      let left = optionRect.left - containerRect.left - containerRef.current.clientLeft;
      if (activeIndex === options.length - 1) {
        left += 1;
      }
      const width = optionRect.width;

      setSliderStyle({
        transform: `translateX(${left}px)`,
        width: `${width}px`,
      });
    } else if (containerRef.current && options.length > 0) {
      // Fallback: assume equal width options or try to calculate based on existing refs
      const validRefs = optionRefs.current.filter(ref => ref && ref.offsetWidth > 0);

      if (validRefs.length === options.length && !fitContent) {
        // All refs are valid and we're using equal width, calculate positions
        const containerWidth = containerRef.current.offsetWidth;
        const optionWidth = containerWidth / options.length;
        let left = (activeIndex * optionWidth) - containerRef.current.clientLeft;
        if (activeIndex === options.length - 1) {
          left += 1;
        }

        setSliderStyle({
          transform: `translateX(${left}px)`,
          width: `${optionWidth}px`,
        });
      } else if (validRefs.length > 0 && fitContent) {
        // Some refs are valid and we're using fitContent, try to position based on available refs
        let accumulatedWidth = 0;
        for (let i = 0; i < activeIndex && i < validRefs.length; i++) {
          if (validRefs[i]) {
            accumulatedWidth += validRefs[i].offsetWidth;
          }
        }

        const activeRef = validRefs[activeIndex];
        if (activeRef) {
          setSliderStyle({
            transform: `translateX(${accumulatedWidth}px)`,
            width: `${activeRef.offsetWidth}px`,
          });
        }
      }
    }
  }, [activeIndex, options.length, fitContent]);

  // Calculate equal width immediately when refs are available
  useLayoutEffect(() => {
    const checkRefsAndCalculate = () => {
      if (optionRefs.current.length > 0 && optionRefs.current.every(ref => ref !== null) && !isInitialized) {
        calculateEqualWidth();
      }
    };

    // Check immediately
    checkRefsAndCalculate();

    // Also check after a short delay in case refs aren't available yet
    const timeoutId = setTimeout(checkRefsAndCalculate, 0);
    return () => clearTimeout(timeoutId);
  }, [calculateEqualWidth, options, isInitialized]);

  // Update position when refs are ready or active index changes
  useLayoutEffect(() => {
    // Use setTimeout to ensure DOM has updated after state changes
    const timeoutId = setTimeout(() => {
      updateSliderPosition();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [updateSliderPosition]);

  // Initial position calculation
  useEffect(() => {
    updateSliderPosition();
  }, [updateSliderPosition]);

  // Recalculate when container size changes (e.g., tab becomes visible)
  useEffect(() => {
    if (!containerRef.current) return;

    if (typeof ResizeObserver === 'undefined') {
      const timeoutId = setTimeout(() => {
        updateSliderPosition();
      }, 0);
      return () => clearTimeout(timeoutId);
    }

    const resizeObserver = new ResizeObserver(() => {
      updateSliderPosition();
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [updateSliderPosition]);

  // Update position on window resize
  useEffect(() => {
    const handleResize = () => {
      updateSliderPosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateSliderPosition]);

  const handleOptionClick = (option, index) => {
    if (disabled || option.disabled) return;

    setActiveIndex(index);
    onChange?.(option.value);
  };

  const classes = [
    'revolt-segmented-control',
    `revolt-segmented-control-${size}`,
    disabled ? 'is-disabled' : '',
    !isInitialized ? 'is-calculating' : '',
    className
  ].filter(Boolean).join(' ');

  const sliderClasses = [
    'revolt-segmented-slider',
    disableAnimation ? 'is-static' : ''
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={containerRef}
      className={classes}
      role="radiogroup"
      {...props}
    >
      <div className={sliderClasses} style={sliderStyle} />
      {options.map((option, index) => {
        const isActive = index === activeIndex;
        const isOptionDisabled = disabled || option.disabled;

        return (
          <button
            key={option.value}
            ref={el => optionRefs.current[index] = el}
            type="button"
            className={`revolt-segmented-option ${isActive ? 'is-active' : ''} ${isOptionDisabled ? 'is-disabled' : ''}`}
            onClick={() => handleOptionClick(option, index)}
            disabled={isOptionDisabled}
            role="radio"
            aria-checked={isActive}
            aria-disabled={isOptionDisabled}
            style={isInitialized && !fitContent ? { width: `${equalWidth}px` } : {}}
          >
            {option.icon && <span className="revolt-segmented-icon">{option.icon}</span>}
            <span className="revolt-segmented-label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}